import { IServerConnection, WebSocketServerConnection, IRetryPolicySettings } from '@/app-modules/server-communications';
import { inject, injectable } from 'inversify';
import { Subject } from 'rxjs/Subject';
import { IGameController } from '@/components/game/_domain/models/game-controller';
import { IGameServerService, IServerServiceFactory, IGameServerServiceConfiguration, IAuthenticationResponse, ServerServiceSubscriptionStore, IJsonGameServerService } from '@/app-modules/server-service';
import { CatchUpData, ResetData, WinnersData, AuthenticationCommand, CallsData, NextGameData, BoxNumbersData } from '@/components/game/_domain/server';
import {
  GameIdentity, IGameConfiguration, IGame, GameState, IGameRepresentation, GameCallboard,
  IGameStageWinnerSet, IGameWonArgs, IPlaySoundArgs, IGameConnectionStateChangedArgs, IGameStateChangedArgs,
  IGameRepresentationChangedArgs, GameConnectionStateChangedFunc, GameStateChangedFunc,
  GameRepresentationChangedFunc, GameWonFunc, PlaySoundFunc, IGamePrizeWinner, BoxNumberCallboard
} from '@/components/game/_domain/models';
import { GameServerResponseStreams } from '@/components/game/_domain/server/_factory/stream-factory';
import { IConfigurationService, ConfigurationKey, ConfigurationPaths, IChatModsConfiguration, IChatModsGameConfigurationPaths } from '@/app-modules/config';
import { User } from '@/app-modules/store/modules/user';
import { safeCast } from '@/helper';
import { PrizeWinnerData } from '@/components/game/_domain/server/prize-winner/prize-winner-data';
import { IWinnersTrackerService, ITrackedWinner } from '@/components/game/_domain/modules/winners-tracker-service';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { Sounds } from '@/app-modules/sound-service';
import { IErrorService } from '@/app-modules/error';

class GameConfiguration implements IGameConfiguration {
  public readonly maxNumberOfCallsPerGame: number;
  public readonly showCallsAsImages: boolean;
  public readonly callSkins: string[];
  public readonly jackpotValues: number[];
  public readonly callNames: string[] = [];
  public readonly initialCallValue: number;

  public constructor(private readonly gameConfig: IChatModsConfiguration, private readonly gameConfigPaths: IChatModsGameConfigurationPaths, public readonly gameViewComponentName: string) {
    this.maxNumberOfCallsPerGame = this.getMaxNumberOfCallsPerGame();
    this.showCallsAsImages = this.getCallsAsImages();
    this.callSkins = this.getCallSkins();
    this.jackpotValues = this.getJackpotValues();
    this.callNames = this.getCallNames();
    this.initialCallValue = this.getInitialCallValue();
  }

  private getMaxNumberOfCallsPerGame(): number {
    return safeCast(this.getConfiguration('ballcount'), 50);
  }

  private getCallsAsImages(): boolean {
    return safeCast(this.getConfiguration('callsasimages'), false);
  }

  private getCallSkins(): string[] {
    const skins: string = this.getConfiguration('skins');
    return skins ? skins.split(',') : [];
  }

  private getJackpotValues(): number[] {
    const jackpotValues: string = this.getConfiguration('jackpotprize');
    return jackpotValues ? jackpotValues.split(',').map(Number) : [];
  }

  private getCallNames(): string[] {
    const names: string = this.getConfiguration('callnames');
    return names ? names.split(',') : [];
  }

  private getInitialCallValue(): number {
    const zeroIsValidCall: boolean = safeCast(this.getConfiguration('zeroIsValidCall'), false);
    return zeroIsValidCall ? 0 : 1;
  }

  private getConfiguration(configKey: keyof IChatModsGameConfigurationPaths): string {
    const configPath = this.gameConfigPaths[configKey];
    return this.gameConfig.getConfigurationByPath(configPath) as string;
  }
}

class Game implements IGame {
  public constructor(public readonly gameIdentity: GameIdentity, public readonly gameConfiguration: IGameConfiguration) {
    this.connectionDebugInfo = 'Disconnected';
    this.state = GameState.InPlay;
  }

  public connectionDebugInfo: string;
  public state: GameState;
  public representation?: IGameRepresentation;

  public updateRepresentation(newGameRepresentation: Partial<IGameRepresentation>): void {
    this.representation = Object.assign({}, this.representation, newGameRepresentation || {});
  }
}

@injectable()
class BaseGameController implements IGameController {
  protected game: Game;
  protected gameServerService: IGameServerService;
  protected readonly onGameWonObservable = new Subject<IGameWonArgs>();
  protected readonly onPlaySoundObservable = new Subject<IPlaySoundArgs>();
  private connection: IServerConnection;
  private gameRetryPolicySettings: IRetryPolicySettings;
  private currentUser: User;
  private readonly onGameConnectionStateChangedObservable = new Subject<IGameConnectionStateChangedArgs>();
  private readonly onGameStateChangedObservable = new Subject<IGameStateChangedArgs>();
  private readonly onGameRepresentationChangedObservable = new Subject<IGameRepresentationChangedArgs>();
  protected readonly onNextGameRepresentationChangedObservable = new Subject<IGameRepresentationChangedArgs>();
  protected readonly displayWinnersForInMilliseconds: number;
  protected readonly serverSubscriptionStore = new ServerServiceSubscriptionStore();

  public constructor(
    @inject(SERVICE_IDENTIFIERS.IConfigurationService) protected readonly configurationService: IConfigurationService,
    @inject(SERVICE_IDENTIFIERS.IServerServiceFactory) private readonly serverServiceFactory: IServerServiceFactory,
    @inject(SERVICE_IDENTIFIERS.IWinnersTrackerService) protected readonly winnersTrackerService: IWinnersTrackerService,
    @inject(SERVICE_IDENTIFIERS.IErrorService) private readonly errorService: IErrorService
  ) {
    this.displayWinnersForInMilliseconds = this.configurationService.getConfigurationItem(ConfigurationKey.GAME_WINNERS_DISPLAY_TIMEOUT_MS);
  }

  public initialise(gameIdentity: GameIdentity) {
    this.game = this.getInitialGame(gameIdentity);
    this.initialiseGameServerConnection();
    this.initialiseGameRetryPolicySettings();
    this.initialiseGameServerService();
  }

  protected initialiseGameServerConnection() {
    this.connection = this.getWebSocketServerConnection();
  }

  protected initialiseGameRetryPolicySettings() {
    this.gameRetryPolicySettings = this.getGameRetryPolicySettings();
  }

  protected initialiseGameServerService() {
    const gameName = this.game.gameIdentity.nameForConfiguration;
    const gameServerResponseStreamsResolver = GameServerResponseStreams.createStreamsForGame(this.game.gameIdentity);
    const gameServerServiceConfiguration: IGameServerServiceConfiguration = {
      connection: this.connection,
      createStreams: gameServerResponseStreamsResolver,
      retryPolicySettings: this.gameRetryPolicySettings,
      gameName
    };
    this.gameServerService = this.serverServiceFactory.createGameService(gameServerServiceConfiguration);
  }

  public connectToGameServer(currentUser: User): IGameController {
    if (this.gameServerService === null) {
      throw new Error('server not initialised');
    }

    this.currentUser = currentUser;
    this.subscribeToGameEvents();
    this.gameServerService.subscribeConnected(this.handleConnectedMessage.bind(this));
    this.gameServerService.subscribeAuth(this.handleAuthorisationMessage.bind(this));
    this.gameServerService.subscribeRetryCircuitBroken(this.handleRetryCircuitBroken.bind(this));
    this.gameServerService.connect();
    return this;
  }

  protected subscribeToGameEvents(): void {
    this.serverSubscriptionStore.push(
      this.gameServerService.subscribeCatchUp(this.handleCatchUpMessage.bind(this)),
      this.gameServerService.subscribeWinners(this.handleWinnersMessage.bind(this)),
      this.gameServerService.subscribeReset(this.handleResetMessage.bind(this)),
      this.gameServerService.subscribeCalls(this.handleCallsMessage.bind(this)),
      this.gameServerService.subscribeNextGame(this.handleNextGameMessage.bind(this)),
      this.gameServerService.subscribePrizeWinner(this.handlePrizeWinnerMessage.bind(this))
    );
  }

  public disconnectFromGameServer(): void {
    if (this.gameServerService === null) {
      throw new Error('server not initialised');
    }
    this.gameServerService.disconnect(false);
    this.changeGameConnectionState('Disconnected');
  }

  public getGame(): IGame {
    return this.game;
  }

  public getRetryPolicySettings(): IRetryPolicySettings {
    return this.gameRetryPolicySettings;
  }

  public getServerService(): IGameServerService {
    return this.gameServerService;
  }

  public getJsonServerService(): IJsonGameServerService | null {
    return null;
  }

  public dispose(): void {
    this.serverSubscriptionStore.unsubscribeAll();
    this.onGameStateChangedObservable.unsubscribe();
    this.onGameRepresentationChangedObservable.unsubscribe();
    this.onNextGameRepresentationChangedObservable.unsubscribe();
    this.onPlaySoundObservable.unsubscribe();
    this.connection.disconnect(false);
  }

  public onGameConnectionStateChanged(callback: GameConnectionStateChangedFunc): IGameController {
    this.onGameConnectionStateChangedObservable.subscribe(callback);
    return this;
  }

  public onGameStateChanged(callback: GameStateChangedFunc): IGameController {
    this.onGameStateChangedObservable.subscribe(callback);
    return this;
  }

  public onGameRepresentationChanged(callback: GameRepresentationChangedFunc): IGameController {
    this.onGameRepresentationChangedObservable.subscribe(callback);
    return this;
  }

  public onNextGameRepresentationChanged(callback: GameRepresentationChangedFunc): IGameController {
    this.onNextGameRepresentationChangedObservable.subscribe(callback);
    return this;
  }

  public onGameWon(callback: GameWonFunc): IGameController {
    this.onGameWonObservable.subscribe(callback);
    return this;
  }

  public onPlaySound(callback: PlaySoundFunc): IGameController {
    this.onPlaySoundObservable.subscribe(callback);
    return this;
  }

  protected getInitialGame(gameIdentity: GameIdentity): Game {
    const gameConfig = this.getGameConfiguration(gameIdentity);
    const maxNumberOfCallsPerGame = gameConfig.maxNumberOfCallsPerGame;
    const game = new Game(gameIdentity, gameConfig);
    game.representation = {
      callboard: new GameCallboard(maxNumberOfCallsPerGame, gameConfig.callNames, gameConfig.initialCallValue),
      boxNumberCallboard: new BoxNumberCallboard(),
    };
    return game;
  }

  protected getGameConfiguration(gameIdentity: GameIdentity): IGameConfiguration {
    const config = this.configurationService.getConfigurationSync();

    const gameConfigPaths = ConfigurationPaths.forGame(gameIdentity.nameForConfiguration);
    return new GameConfiguration(config, gameConfigPaths, gameIdentity.nameForConfiguration + 'GameView');
  }

  private getWebSocketServerConnection(): IServerConnection {
    const gameServerUrl = this.getGameWebSocketServerUrl();
    return this.serverServiceFactory.createGameServerConnection(gameServerUrl, this.game.gameIdentity.name);
  }

  private getGameWebSocketServerUrl(): string {
    if (this.game.gameIdentity.name === 'Britains Biggest Bingo') {
      return this.getGameWebSocketServerUrlNewFormat();
    }
    const gameServerUrl = this.configurationService.getConfigurationItem(ConfigurationKey.GAME_WEBSOCKET_SERVER_URL);
    const gameName = this.game.gameIdentity.nameForConfiguration;
    return `${gameServerUrl}${gameName}`;
  }

  private getGameWebSocketServerUrlNewFormat(): string {
    const gameServerUrl = this.configurationService.getConfigurationItem(ConfigurationKey.GAME_NEW_WEBSOCKET_SERVER_URL);
    const gamename = `-${this.game.gameIdentity.nameForConfiguration}`;
    return gameServerUrl.format({ gamename });
  }
  protected getGameRetryPolicySettings(): IRetryPolicySettings {
    return {
      enabled: true,
      maxRetries: 5
    };
  }

  protected handleCatchUpMessage(payload: CatchUpData): void {
    this.game.representation.callboard.update(payload.callValues);
    this.changeGameRepresentation({
      gameId: payload.gameNo,
      numberOfPlayers: payload.numberOfPlayers,
      isLinkedGame: payload.isLinkedGame,
      promoType: payload.promoType,
      promoMultiplier: payload.promoMultiplier,
      prizeStructure: payload.prizeStructure
    });

    // TODO: tidy up comparison to use strong typing - gameStage of 0 is the purchasing stage
    const newGameState = payload.gameStage === 0 ? GameState.Purchasing : GameState.InPlay;
    this.changeGameState(newGameState);
  }

  protected handleWinnersMessage(payload: WinnersData): void {
    const unannouncedWinners = this.getUnannouncedGameStageWinnerSetsFromWinnersData(payload);

    this.changeGameRepresentation({
      isJackpotWon: payload.jackpotWon,
      winners: payload.winners
    });
    this.notifyGameWon();
    this.changeGameState(GameState.WinnersDisplayed);
    this.trackPrizeWinners(unannouncedWinners);

    setTimeout(() => {
      this.changeGameState(GameState.Purchasing);
    }, this.displayWinnersForInMilliseconds);
  }

  protected handleResetMessage(payload: ResetData): void {
    this.game.representation.callboard.reset();
    this.changeGameState(GameState.InPlay);
    this.changeGameRepresentation({
      gameId: payload.gameId,
      numberOfPlayers: payload.numberOfPlayers,
      promoType: payload.promoType,
      promoMultiplier: payload.promoMultiplier,
      nextGame: null,
      isJackpotWon: null,
      prizeStructure: payload.prizeStructure,
      winners: null
    });
    this.notifyPlaySoundRequest();
  }

  protected handleCallsMessage(payload: CallsData): void {
    this.game.representation.callboard.update([payload.callValue], payload.callNumber);
    this.notifyGameRepresentationChanged();
  }

  protected handleNextGameMessage(payload: NextGameData): void {
    this.game.updateRepresentation({
      nextGame: {
        countDownToNextGameInSeconds: payload.time,
        isLinkedGame: payload.isLinkedGame,
        promoType: payload.promoType,
        prizeStructure: payload.prizeStructure
      }
    });
    this.notifyNextGameRepresentationChanged();
  }

  protected handlePrizeWinnerMessage(payload: PrizeWinnerData): void {
    const currentWinnerSets = this.game.representation.winners || [];
    const payloadWinnerSets = this.getGameStageWinnerSetsFromPrizeWinnerData(payload);
    this.changeGameRepresentation({
      winners: this.mergeGameStageWinnerSets(currentWinnerSets, payloadWinnerSets),
      isJackpotWon: payload.jackpot
    });
    this.notifyGameWon();
    this.trackPrizeWinners(payloadWinnerSets);
  }

  protected getGameStageWinnerSetsFromPrizeWinnerData(payload: PrizeWinnerData): IGameStageWinnerSet[] {
    const stageWinnerSets: { [key: string]: IGameStageWinnerSet } = {};
    payload.winners.forEach((winner) => {
      let gameStageWinnerSet = stageWinnerSets[winner.prizeName];
      if (!gameStageWinnerSet) {
        gameStageWinnerSet = {
          stage: winner.stage,
          prizeName: winner.prizeName,
          winners: []
        };
        stageWinnerSets[winner.prizeName] = gameStageWinnerSet;
      }

      gameStageWinnerSet.winners.push({
        username: winner.username,
        prizeInPence: winner.prizeInPence
      });
    });

    return Object.keys(stageWinnerSets).map((prizeName) => stageWinnerSets[prizeName]);
  }

  private getUnannouncedGameStageWinnerSetsFromWinnersData(payload: WinnersData): IGameStageWinnerSet[] {
    const unannouncedWinnerSets: IGameStageWinnerSet[] = [];
    const currentWinnerSets: IGameStageWinnerSet[] = this.game.representation.winners || [];
    const payloadWinnerSets: IGameStageWinnerSet[] = payload.winners || [];
    payloadWinnerSets.forEach((payloadWinnerSet) => {
      let currentWinnerSet = currentWinnerSets.find((o) => o.stage === payloadWinnerSet.stage);
      if (!currentWinnerSet) {
        currentWinnerSet = {
          stage: payloadWinnerSet.stage,
          prizeName: payloadWinnerSet.prizeName,
          winners: []
        };
      }

      const unannouncedWinners = this.getUnannouncedWinnersFromGameStageWinnerSet(currentWinnerSet, payloadWinnerSet);
      unannouncedWinnerSets.push({
        stage: currentWinnerSet.stage,
        prizeName: currentWinnerSet.prizeName,
        winners: unannouncedWinners
      });
    });
    return unannouncedWinnerSets;
  }

  private getUnannouncedWinnersFromGameStageWinnerSet(currentWinnerSet: IGameStageWinnerSet, payloadWinnerSet: IGameStageWinnerSet): IGamePrizeWinner[] {
    const currentWinners = currentWinnerSet.winners || [];
    const payloadWinners = payloadWinnerSet.winners || [];
    return payloadWinners.filter((payloadWinner, index) => index >= currentWinners.length);
  }

  protected mergeGameStageWinnerSets(destinationWinnerSets: IGameStageWinnerSet[], sourceWinnerSets: IGameStageWinnerSet[]): IGameStageWinnerSet[] {
    const gameStageWinnerSets: IGameStageWinnerSet[] = [];
    gameStageWinnerSets.push(...destinationWinnerSets);
    for (const winnerSet of sourceWinnerSets) {
      const existingWinnerSet = gameStageWinnerSets.find((o) => o.prizeName === winnerSet.prizeName);
      if (existingWinnerSet) {
        if (existingWinnerSet.ticketBooks) {
          existingWinnerSet.ticketBooks.push(...(winnerSet.ticketBooks || []));
        }

        if (existingWinnerSet.winners) {
          existingWinnerSet.winners.push(...(winnerSet.winners || []));
        }
      }
      else {
        gameStageWinnerSets.push(winnerSet);
      }
    }
    return gameStageWinnerSets;
  }

  protected trackPrizeWinners(winnerSets: IGameStageWinnerSet[]): void {
    const gameIdentity = this.game.gameIdentity;
    winnerSets.forEach((winnerSet) => {
      const prizeName = winnerSet.prizeName;
      const trackedWinners = (winnerSet.winners || []).map<ITrackedWinner>((winner) => ({
        gameName: gameIdentity.name,
        clubName: gameIdentity.clubName,
        username: winner.username,
        prizeInPence: winner.prizeInPence,
        prizeName
      }));
      this.winnersTrackerService.trackWinners(...trackedWinners);
    });
  }

  protected handleConnectedMessage(): void {
    this.changeGameConnectionState('Connected');
    this.authToGame();
  }

  protected handleAuthorisationMessage(payload: IAuthenticationResponse): void {
    this.changeGameConnectionState('Authorised');
  }

  protected handleRetryCircuitBroken(): void {
    const err = new Error(`Game socket connection lost for ${this.getGame().gameIdentity.nameForConfiguration}`);
    this.errorService.logError(err);
  }

  protected notifyGameWon() {
    this.notifyIfOpen(this.onGameWonObservable, {
      gameWinnerInfo: this.game.representation.winners,
      isJackpotWon: this.game.representation.isJackpotWon,
    });
    this.notifyPlaySoundRequest();
  }

  protected changeGameState(gameState: GameState): void {
    this.game.state = gameState;
    this.notifyGameStateChanged();
  }

  protected changeGameRepresentation(newGameRepresentation: Partial<IGameRepresentation>): void {
    this.game.updateRepresentation(newGameRepresentation);
    this.notifyGameRepresentationChanged();
  }

  private authToGame(): void {
    const authCmd = new AuthenticationCommand(this.currentUser, this.game.gameIdentity.clubId);
    this.gameServerService.send(authCmd);
  }

  protected changeGameConnectionState(connectionState: string): void {
    this.game.connectionDebugInfo = connectionState;
    this.notifyGameConnectionStateChanged();
  }

  private notifyGameConnectionStateChanged() {
    this.notifyIfOpen(this.onGameConnectionStateChangedObservable, {
      currentConnectionState: this.game.connectionDebugInfo
    });
  }

  private notifyGameStateChanged() {
    this.notifyIfOpen(this.onGameStateChangedObservable, {
      currentGameState: this.game.state
    });
  }

  protected notifyGameRepresentationChanged() {
    this.notifyIfOpen(this.onGameRepresentationChangedObservable, {
      currentGameRepresentation: this.game.representation
    });
  }

  protected notifyNextGameRepresentationChanged() {
    this.notifyIfOpen(this.onNextGameRepresentationChangedObservable, {
      currentGameRepresentation: this.game.representation
    });
  }

  protected notifyPlaySoundRequest() {
    this.notifyIfOpen(this.onPlaySoundObservable, {
      sound: Sounds.NOTIFICATION
    });
  }

  private notifyIfOpen<T>(subject: Subject<T>, value?: T): void {
    if (subject.closed) {
      return;
    }

    subject.next(value);
  }
}

export { BaseGameController, GameConfiguration };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/game/implementations/_base/_domain/controller/game-controller.ts